import React, { useEffect, useState } from 'react'
import LinearProgress from '@mui/material/LinearProgress';
import io from 'socket.io-client';
import useCloudinary from '../../hooks/useCloudinary';
import { readBarcodesFromImageWasm } from '../../utils/wasmBarcodeReader';
import CircularProgress from '@mui/material/CircularProgress';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import useManageWeeklyShip from './useManageWeeklyShip';

// QR from print_server.py: `${weight}|${trackingJp}|${orderId}|${boxNo}|${shipDate}`
const parsePipeQR = (codes) => {
  for (const c of codes || []) {
    const t = (c?.text || '').trim();
    if (!t.includes('|')) continue;
    const parts = t.split('|');
    if (parts.length !== 5) continue;
    const [weightStr, trackingJp, , boxNo] = parts;
    const weight = parseFloat(weightStr);
    if (!isNaN(weight) && boxNo) {
      return { weight, trackingJp: trackingJp || null, boxNo: String(boxNo).trim() };
    }
  }
  return null;
};

// Hold QR from print_server.py: `HOLD|${holdTag}|${trackingJp}|${weight}|` (5 fields).
// A held box has no shipDate/boxNo, so its photo binds by tag instead of the boxNo path.
const parseHoldQR = (codes) => {
  for (const c of codes || []) {
    const parts = (c?.text || '').trim().split('|');
    if (parts.length === 5 && parts[0] === 'HOLD' && parts[1]) {
      return { holdTag: parts[1].trim() };
    }
  }
  return null;
};

const WeeklyShipImageUpload = ({shipDate, shipDateId}) => {
  const [files, setFiles] = useState(null);
  const [uploadStatus, setUploadStatus] = useState("");
  const [isLoading, setIsLoading] = useState(false)
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [progress, setProgress] = useState(0);
  const [uploadedCount, setUploadedCount] = useState(0); // New State
  const [totalCount, setTotalCount] = useState(0); // New State
  const [isUnpack, setIsUnpack] = useState(false); // Upload as unpacked-contents photos, not box photos
  const shopName = useSelector((state) => state.shop.name);

  const {uploadWeeklyShipImage, uploadAirShipImage, error} = useCloudinary()
  const { createWeeklyShipAir, attachImageToHeldBox } = useManageWeeklyShip()

  const handleFileChange = (e) => {
    setFiles([...e.target.files]);
    setTotalCount(e.target.files.length); 
    setUploadedCount(0)
    setProgress(0)
  };

  const handleUpload = async () => {
    setIsLoading(true)
    // Unpack photos always go through the plain upload (no box QR to decode).
    // Determine if Air by label text (value contains 'เครื่องบิน')
    const isAir = !isUnpack && typeof shipDate === 'string' && shipDate.includes('เครื่องบิน');
    if (isAir) {
      // Decode all files client-side. Keep raw results for QR parsing; send cleaned (≤30 chars) to backend for storage.
      const isOdaJ4410 = typeof shipDate === 'string' && (shipDate.includes('ODA J4410') || shipDate.includes('D2U'));
      const filesWithCodes = [];
      const parsedQRByIndex = [];
      const parsedHoldByIndex = [];
      for (const file of files || []) {
        try {
          const results = await readBarcodesFromImageWasm(file, { requireSystemQR: isOdaJ4410 });
          // Keep short codes AND the system pipe-QR (weight|trackingJp|orderId|boxNo|shipDate),
          // which exceeds 30 chars whenever the parcel carries a tracking number — without it
          // the backend never sees the boxNo and the image can't auto-attach.
          const isSystemQR = (t) => (t || '').split('|').length === 5;
          const cleanedCodes = results.filter(
            (code) => code.text && (code.text.length <= 30 || isSystemQR(code.text))
          );
          filesWithCodes.push({
            name: file.name,
            size: file.size,
            type: file.type,
            codes: cleanedCodes,
            originalCount: results.length,
            filteredCount: cleanedCodes.length
          });
          parsedQRByIndex.push(isOdaJ4410 ? parsePipeQR(results) : null);
          parsedHoldByIndex.push(isOdaJ4410 ? parseHoldQR(results) : null);
        } catch (e) {
          filesWithCodes.push({
            name: file.name,
            size: file.size,
            type: file.type,
            codes: [],
            error: e?.message || 'decode failed'
          });
          parsedQRByIndex.push(null);
          parsedHoldByIndex.push(null);
        }
      }

      const formData = new FormData();
      (files || []).forEach((file) => formData.append('images', file));
      formData.append('shipDateId', shipDateId);
      formData.append('shop', shopName);
      formData.append('codes', JSON.stringify(filesWithCodes));

      const result = await uploadAirShipImage(formData);
      if (!result) {
        setUploadStatus('Air upload failed');
        setIsLoading(false);
        return;
      }

      if (isOdaJ4410) {
        const shipImageIds = Array.isArray(result.shipImageIds) ? result.shipImageIds : [];
        let created = 0;
        let qrMatched = 0;
        const failures = [];
        for (let i = 0; i < parsedQRByIndex.length; i++) {
          const parsed = parsedQRByIndex[i];
          const hold = parsedHoldByIndex[i];
          const shipImageId = shipImageIds[i];
          if (!shipImageId) continue;
          // Hold photo → bind to the held box by tag (it has no boxNo/shipDate).
          if (hold) {
            qrMatched += 1;
            const resp = await attachImageToHeldBox({ shipImageId, holdTag: hold.holdTag });
            if (resp?.success) {
              created += 1;
            } else {
              failures.push(`hold ${hold.holdTag}: ${resp?.message || 'failed'}`);
            }
            continue;
          }
          if (!parsed) continue;
          qrMatched += 1;
          const resp = await createWeeklyShipAir({
            shipImageId,
            shipDateId,
            boxNo: parsed.boxNo,
            weight: parsed.weight,
            trackingJp: parsed.trackingJp,
          });
          if (resp?.success) {
            created += 1;
          } else {
            failures.push(`box ${parsed.boxNo}: ${resp?.message || 'failed'}`);
          }
        }
        const total = parsedQRByIndex.length;
        const manual = total - qrMatched;
        toast.success(
          `อัปโหลด ${total} รูป — สร้าง WeeklyShip อัตโนมัติ ${created}/${qrMatched} · รอกรอกมือ ${manual}`
        );
        if (failures.length) {
          toast.warning(`สร้างไม่สำเร็จ ${failures.length} รายการ: ${failures.slice(0, 3).join('; ')}${failures.length > 3 ? '…' : ''}`);
        }
        setUploadStatus(`Air upload OK — auto ${created}/${qrMatched}, manual ${manual}`);
      } else {
        setUploadStatus('Air upload & decode success!');
      }
      setIsLoading(false);
      return;
    } else {
      const formData = new FormData();
      (files || []).forEach((file) => {
          formData.append('images', file);
      });
      formData.append('shipDateId', shipDateId);
      formData.append('shop', shopName);
      if (isUnpack) formData.append('type', 'unpack');

      for (let [key, value] of formData.entries()) {
        console.log(key, value);
      }
      const result = await uploadWeeklyShipImage(formData)
      console.log(result)
      if(result){
        setUploadStatus("Upload Success !!")
      }
    }
  };

  useEffect(() => {
    // withCredentials so the admin_token cookie rides the handshake: every
    // namespace including the root one is admin-only now (B5), and without
    // it the connection is refused and the progress bar never moves.
    const socket = io(BACKEND_URL, { withCredentials: true });
    socket.on('progress', (data) => {
        setProgress(data.progress);
        setUploadedCount(data.uploadedCount); // Update uploaded count on progress
    });
    return () => {
        socket.disconnect();
    };
  }, []);

  useEffect(() => {
    if(progress === 0 || progress === 100){
      setIsLoading(false)
    }else{
      setIsLoading(true)
    }
  },[uploadedCount])

  return (
    <div>
      <label className='flex items-center gap-2 my-2 select-none'>
        <input type="checkbox" checked={isUnpack} onChange={(e) => setIsUnpack(e.target.checked)} />
        <span>รูปแกะกล่อง (unpack) — ไม่ใช่รูปกล่อง</span>
      </label>
      <input className='my-2' type="file" name="images" multiple onChange={handleFileChange} />
      <button className="btn-primary" disabled={isLoading} onClick={handleUpload}>Upload</button>
      <LinearProgress variant="determinate" value={progress} />
      {uploadedCount} / {totalCount} {/* Display the upload count */}
      {uploadStatus && <p>{uploadStatus} {error && <span className='text-red-600'>{error}</span>}</p>}
      {isLoading && <CircularProgress />} {/* Render spinner when loading */}
    </div>
  );
};

export default WeeklyShipImageUpload